import {
  GetResponse,
  WebLoginPreferencesInterface,
  SetRequest,
  SetResponse,
  Username,
} from '@central-products/interface-web-login-preferences';
import { createCasClient, CasClient } from '@central-products/cas-client';
import { Maybe } from './types/index';
import { errors } from './errors';
import { createErrorWithoutState } from './utils';

const TWO_SECONDS = 2000;

export interface WebLoginPreferencesConfig {
  serviceUrl: string;
  casUrl: string;
  enableFlagHandling: boolean;
  enableRecaptcha: boolean;
  enableSessionRenew: boolean;
}

export class WebLoginPreferences extends WebLoginPreferencesInterface {
  get KeepLoggedIn(): boolean {
    return this.keepLoggedIn;
  }

  get RememberUsername(): Maybe<Username> {
    return this.rememberUsername;
  }
  private keepLoggedIn: boolean = false;
  private rememberUsername?: Username;
  private isPreferencesSet: boolean = false;
  private casClient: CasClient;
  private retryTimeout?: number | null;
  private enableSessionRenew: boolean = false;
  private readonly retryInterval: number;

  constructor({
    casUrl,
    serviceUrl,
    enableFlagHandling = false,
    enableRecaptcha = false,
    enableSessionRenew = false,
  }: WebLoginPreferencesConfig) {
    super();
    this.enableSessionRenew = enableSessionRenew;

    this.casClient = createCasClient({ casUrl, serviceUrl, enableFlagHandling, enableRecaptcha });
    this.retryInterval = TWO_SECONDS;
  }

  public async get(): Promise<GetResponse> {
    if (this.enableSessionRenew === false) {
      const isLoggedIn = await this.callIamAlive()
        .then((r) => r)
        .catch(() => false);
      if (isLoggedIn) return Promise.reject(createErrorWithoutState(errors.WebLoginPreferences.get.stateInvalid));
    }

    try {
      const response = await this.casClient.getFormDefaults({ renew: this.enableSessionRenew });
      if (this.isPreferencesSet) {
        const preferences = this.getPreferences();
        this.resetPreferences();

        return preferences;
      }

      const { keepMeLoggedIn, username, rememberUsername } = response.form_defaults;
      this.keepLoggedIn = keepMeLoggedIn;
      if (rememberUsername && username) {
        this.rememberUsername = { username };
      }
    } catch (error) {
      return Promise.reject(createErrorWithoutState(errors.WebLoginPreferences.get.operationFailed));
    }

    return this.getPreferences();
  }

  public async set(data: SetRequest): Promise<SetResponse> {
    this.keepLoggedIn = data.keepLoggedIn;
    this.rememberUsername = data.rememberUsername;
    this.isPreferencesSet = true;

    return {};
  }

  private retry = () =>
    new Promise<boolean>((resolve) => {
      this.retryTimeout = window.setTimeout(() => {
        this.retryTimeout = null;
        this.callIamAlive().then(resolve);
      }, this.retryInterval);
    });

  private clearRetryTimer = () => {
    if (this.retryTimeout) {
      window.clearInterval(this.retryTimeout);
    }
  };

  private callIamAlive = () =>
    new Promise<boolean>((resolve) => {
      this.casClient
        .imAlive()
        .then(({ imAlive }) => {
          this.clearRetryTimer();
          resolve(imAlive);
        })
        .catch(({ status }: { status?: number }) => {
          if (!status) {
            this.retry().then(resolve);
          } else {
            this.clearRetryTimer();
            resolve(false);
          }
        });
    });

  private async getPreferences(): Promise<GetResponse> {
    return {
      keepLoggedIn: this.keepLoggedIn,
      rememberUsername: this.rememberUsername,
    };
  }

  private resetPreferences() {
    this.isPreferencesSet = false;
    this.keepLoggedIn = false;
    this.rememberUsername = undefined;
  }
}
